import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Eye, EyeOff, KeyRound, ShieldX } from "lucide-react";
import { Button, Card, Field, Input, Spinner } from "../components/ui";
import { api } from "../lib/api";

/**
 * Password reset page. The token arrives in the URL *fragment* (after `#`),
 * which browsers never send to the server and which is excluded from the
 * Referer header — so it does not leak into logs or analytics.
 *
 * This page is only reachable by someone holding a valid emailed/WhatsApped
 * link. Nothing on the public site links to it.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [token, setToken] = useState("");
  const [state, setState] = useState<"checking" | "ready" | "invalid" | "done">("checking");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const raw = window.location.hash.replace(/^#/, "");
    const params = new URLSearchParams(raw);
    const t = params.get("token") ?? "";
    // Scrub the token from the address bar once captured.
    if (t) window.history.replaceState(null, "", window.location.pathname);
    setToken(t);
    if (!t) {
      setState("invalid");
      return;
    }
    void (async () => {
      try {
        const res = await api.checkResetToken(t);
        setState(res.valid ? "ready" : "invalid");
      } catch {
        setState("invalid");
      }
    })();
  }, []);

  const submit = async () => {
    setError(null);
    if (password.length < 10) {
      setError("Choose a password with at least 10 characters.");
      return;
    }
    if (password !== confirm) {
      setError("The two passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.resetPassword(token, password);
      setState("done");
      setTimeout(() => navigate("/voting", { replace: true }), 2200);
    } catch (e) {
      setError(e instanceof Error ? e.message : "We could not reset your password. Request a new link.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-black px-5 py-16 text-white">
      <div className="w-full max-w-lg">
        <p className="font-manrope text-[13px] uppercase tracking-[0.3em] text-[#AFDDFF]/80">BBA Freshers 2026</p>

        {state === "checking" && (
          <div className="mt-8">
            <Spinner label="Checking your reset link" />
          </div>
        )}

        {state === "invalid" && (
          <Card className="mt-6 p-6 md:p-8">
            <div className="flex items-start gap-3">
              <ShieldX className="mt-0.5 h-6 w-6 shrink-0 text-red-400" aria-hidden="true" />
              <div>
                <h1 className="text-xl font-semibold">This reset link is not valid</h1>
                <p className="mt-2 text-sm leading-relaxed text-white/60">
                  Reset links expire 30 minutes after they are sent and can only be used once.
                  Request a fresh link from the sign-in page.
                </p>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/voting">
                <Button>Go to sign in</Button>
              </Link>
            </div>
          </Card>
        )}

        {state === "ready" && (
          <Card className="mt-6 p-6 md:p-8">
            <div className="flex items-start gap-3">
              <KeyRound className="mt-0.5 h-6 w-6 shrink-0 text-[#AFDDFF]" aria-hidden="true" />
              <div>
                <h1 className="text-xl font-semibold">Choose a new password</h1>
                <p className="mt-2 text-sm leading-relaxed text-white/60">
                  After saving, you'll be signed out everywhere and can sign in with your new password.
                </p>
              </div>
            </div>

            <div className="mt-6 space-y-4">
              <Field label="New password" htmlFor="rp-password">
                <div className="relative">
                  <Input
                    id="rp-password"
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoComplete="new-password"
                    className="pr-12"
                  />
                  <button
                    type="button"
                    onClick={() => setShow((v) => !v)}
                    aria-label={show ? "Hide password" : "Show password"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded p-1 text-white/50 hover:text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#AFDDFF]/50"
                  >
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <p className="mt-1 text-xs text-white/40">At least 10 characters.</p>
              </Field>
              <Field label="Confirm new password" htmlFor="rp-confirm">
                <Input
                  id="rp-confirm"
                  type={show ? "text" : "password"}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                />
              </Field>
            </div>

            {error && (
              <p className="mt-4 flex items-start gap-2 text-sm text-amber-300">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />{error}
              </p>
            )}

            <div className="mt-6">
              <Button onClick={() => void submit()} disabled={busy}>
                <KeyRound className="h-4 w-4" aria-hidden="true" />
                {busy ? "Saving…" : "Save new password"}
              </Button>
            </div>
          </Card>
        )}

        {state === "done" && (
          <Card className="mt-6 p-6 md:p-8">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-emerald-400" aria-hidden="true" />
              <div>
                <h1 className="text-xl font-semibold">Password updated</h1>
                <p className="mt-2 text-sm leading-relaxed text-white/60">
                  Taking you to the sign-in page so you can log in with your new password.
                </p>
              </div>
            </div>
            <div className="mt-6">
              <Link to="/voting">
                <Button>Continue to sign in</Button>
              </Link>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}
